/**
 * Projectmodel (fase 1 en verder).
 *
 * Harde afspraken:
 *  - Alle posities en lengtes zijn frames op `project.fps`.
 *  - Media staan NIET in dit model. Alleen `assetId`'s. Bestandsnamen,
 *    paden en bytes horen in de `assets`-tabel, niet in de project-JSON.
 *    Daardoor blijft undo/redo klein en blijft het project versioneerbaar.
 *  - `version` is de schemaversie. Verhoging betekent een migratie.
 */

import { z } from 'zod';
import { PROJECT_FPS, STANDAARD_BEELDFORMAAT } from './constanten.js';

export const PROJECT_SCHEMA_VERSIE = 1;

export const beeldformaatIdSchema = z.enum(['16:9', '9:16', '1:1', '4:5']);
export const trackTypeSchema = z.enum(['video', 'afbeelding', 'tekst', 'audio']);
export const passingSchema = z.enum(['bevatten', 'bijsnijden', 'uitrekken']);
export const uitlijningSchema = z.enum(['links', 'midden', 'rechts']);
export const overgangSchema = z.enum(['geen', 'vervagen', 'zwart']);

/** Kleuren als #rrggbb of #rrggbbaa. Bewust streng: komt in CSS én canvas. */
export const kleurSchema = z
  .string()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/, 'Gebruik een hexkleur zoals #ff8800');

const basisItemVelden = {
  id: z.string().min(1),
  startFrame: z.number().int().min(0),
  duurInFrames: z.number().int().min(1),
  /** Naam in de tijdlijn, zodat de gebruiker items kan herkennen. */
  naam: z.string().default(''),
  /** Alleen zichtbaar/actief tussen deze frames. Zelfde eenheid als startFrame. */
  vergrendeld: z.boolean().default(false),
  verborgen: z.boolean().default(false),
};

export const videoItemSchema = z.object({
  ...basisItemVelden,
  soort: z.literal('video'),
  assetId: z.string().min(1),
  /** Uitsnede in de bron, in frames op de project-fps. */
  bronStartFrame: z.number().int().min(0).default(0),
  passing: passingSchema.default('bevatten'),
  volume: z.number().min(0).max(4).default(1),
  gedempt: z.boolean().default(false),
  fadeInFrames: z.number().int().min(0).default(0),
  fadeOutFrames: z.number().int().min(0).default(0),
  overgangIn: overgangSchema.default('geen'),
  overgangDuurInFrames: z.number().int().min(0).default(0),
  snelheid: z.number().min(0.25).max(4).default(1),
});

export const afbeeldingItemSchema = z.object({
  ...basisItemVelden,
  soort: z.literal('afbeelding'),
  assetId: z.string().min(1),
  passing: passingSchema.default('bevatten'),
  /** Relatieve positie en schaal in het kader; 0,5/0,5 is gecentreerd. */
  x: z.number().min(-2).max(3).default(0.5),
  y: z.number().min(-2).max(3).default(0.5),
  schaal: z.number().min(0.01).max(10).default(1),
  /** Langzame schaal tijdens de clip. 'geen' laat de foto stilstaan. */
  beweging: z.enum(['geen', 'inzoomen', 'uitzoomen']).default('geen'),
  rotatie: z.number().min(-360).max(360).default(0),
  dekking: z.number().min(0).max(1).default(1),
});

export const tekstItemSchema = z.object({
  ...basisItemVelden,
  soort: z.literal('tekst'),
  tekst: z.string().max(2000).default(''),
  x: z.number().min(-2).max(3).default(0.5),
  y: z.number().min(-2).max(3).default(0.5),
  fontFamily: z.string().default('Inter'),
  fontSize: z.number().min(4).max(400).default(64),
  fontWeight: z.number().int().min(100).max(900).default(700),
  kleur: kleurSchema.default('#ffffff'),
  uitlijning: uitlijningSchema.default('midden'),
  /** CSS-achtige tekstschaduw voor leesbaarheid op video. */
  schaduw: z.boolean().default(true),
  achtergrondKleur: z.union([kleurSchema, z.literal('transparant')]).default('transparant'),
  /** Maximale regellengte in fractie van de compositiebreedte. */
  maxBreedte: z.number().min(0.05).max(1).default(0.9),
  dekking: z.number().min(0).max(1).default(1),
  rotatie: z.number().min(-360).max(360).default(0),
  /** Optionele animatie; 'geen' = statisch. */
  animatie: z.enum(['geen', 'vervagen', 'omhoog']).default('geen'),
});

export const audioItemSchema = z.object({
  ...basisItemVelden,
  soort: z.literal('audio'),
  assetId: z.string().min(1),
  bronStartFrame: z.number().int().min(0).default(0),
  volume: z.number().min(0).max(4).default(1),
  gedempt: z.boolean().default(false),
  fadeInFrames: z.number().int().min(0).default(0),
  fadeOutFrames: z.number().int().min(0).default(0),
  /** Laat de audio de volledige projectduur lopen (achtergrondmuziek). */
  lus: z.boolean().default(false),
});

export const timelineItemSchema = z.discriminatedUnion('soort', [
  videoItemSchema,
  afbeeldingItemSchema,
  tekstItemSchema,
  audioItemSchema,
]);

export const trackSchema = z.object({
  id: z.string().min(1),
  type: trackTypeSchema,
  naam: z.string().default(''),
  items: z.array(timelineItemSchema).default([]),
  gedempt: z.boolean().default(false),
  verborgen: z.boolean().default(false),
});

export const projectAchtergrondSchema = z.object({
  kleur: kleurSchema.default('#000000'),
});

/**
 * Bouwstenen voor de tekstlaag. De webapp gebruikt deze lijst; de renderer
 * gebruikt alleen de opgeslagen `fontFamily`. Alleen lettertypen die op de
 * server aanwezig zijn (of lokaal worden meegeladen) komen hier voor, zodat
 * een export nooit van internet afhankelijk is.
 */
export const BESCHIKBARE_FONTEN: Array<{ id: string; label: string; stack: string }> = [
  { id: 'Inter', label: 'Inter (standaard)', stack: "'Inter','DejaVu Sans',Arial,sans-serif" },
  { id: 'Arial', label: 'Arial', stack: 'Arial,Helvetica,sans-serif' },
  { id: 'Georgia', label: 'Georgia', stack: 'Georgia,"Times New Roman",serif' },
  { id: 'Impact', label: 'Impact', stack: 'Impact,"Arial Black",sans-serif' },
  { id: 'Courier New', label: 'Courier New', stack: '"Courier New",Courier,monospace' },
  { id: 'Verdana', label: 'Verdana', stack: 'Verdana,Geneva,sans-serif' },
];


export const projectSchema = z.object({
  id: z.string().min(1),
  naam: z.string().min(1).max(200),
  versie: z.number().int().min(1),
  schemaVersie: z.literal(PROJECT_SCHEMA_VERSIE),
  fps: z.number().int().min(1).max(120),
  beeldformaat: beeldformaatIdSchema,
  breedte: z.number().int().min(16).max(4096),
  hoogte: z.number().int().min(16).max(4096),
  achtergrond: projectAchtergrondSchema.default({ kleur: '#000000' }),
  tracks: z.array(trackSchema).max(40),
  /** Handmatige duuroverride; leeg betekent: afgeleid uit de items. */
  duurOverrideInFrames: z.number().int().min(0).nullable().default(null),
  gemaaktOp: z.string(),
  gewijzigdOp: z.string(),
});

export type BeeldformaatIdModel = z.infer<typeof beeldformaatIdSchema>;
export type TrackType = z.infer<typeof trackTypeSchema>;
export type Passing = z.infer<typeof passingSchema>;
export type Uitlijning = z.infer<typeof uitlijningSchema>;
export type Overgang = z.infer<typeof overgangSchema>;
export type VideoItem = z.infer<typeof videoItemSchema>;
export type AfbeeldingItem = z.infer<typeof afbeeldingItemSchema>;
export type TekstItem = z.infer<typeof tekstItemSchema>;
export type AudioItem = z.infer<typeof audioItemSchema>;
export type TimelineItem = z.infer<typeof timelineItemSchema>;
export type Track = z.infer<typeof trackSchema>;
export type ProjectModel = z.infer<typeof projectSchema>;

/** Itemsoorten die beeld opleveren. */
export const BEELD_SOORTEN = ['video', 'afbeelding', 'tekst'] as const;

export function isVideoItem(item: TimelineItem): item is VideoItem {
  return item.soort === 'video';
}
export function isAfbeeldingItem(item: TimelineItem): item is AfbeeldingItem {
  return item.soort === 'afbeelding';
}
export function isTekstItem(item: TimelineItem): item is TekstItem {
  return item.soort === 'tekst';
}
export function isAudioItem(item: TimelineItem): item is AudioItem {
  return item.soort === 'audio';
}

/**
 * Bouw een leeg project met de standaard sporen.
 * De sporen staan vast in deze volgorde: video, afbeelding, tekst, audio.
 */
export function maakLeegProject(opties: {
  id: string;
  naam: string;
  breedte: number;
  hoogte: number;
  beeldformaat?: BeeldformaatIdModel;
  fps?: number;
  nu?: string;
}): ProjectModel {
  const nu = opties.nu ?? new Date().toISOString();
  return {
    id: opties.id,
    naam: opties.naam,
    versie: 1,
    schemaVersie: PROJECT_SCHEMA_VERSIE,
    fps: opties.fps ?? PROJECT_FPS,
    beeldformaat: opties.beeldformaat ?? STANDAARD_BEELDFORMAAT,
    breedte: opties.breedte,
    hoogte: opties.hoogte,
    achtergrond: { kleur: '#000000' },
    tracks: [
      { id: 'track-video', type: 'video', naam: 'Video', items: [], gedempt: false, verborgen: false },
      { id: 'track-afbeelding', type: 'afbeelding', naam: 'Afbeeldingen', items: [], gedempt: false, verborgen: false },
      { id: 'track-tekst', type: 'tekst', naam: 'Tekst', items: [], gedempt: false, verborgen: false },
      { id: 'track-audio', type: 'audio', naam: 'Audio', items: [], gedempt: false, verborgen: false },
    ],
    duurOverrideInFrames: null,
    gemaaktOp: nu,
    gewijzigdOp: nu,
  };
}
